/**
 * @module lib/breakpoints
 * @author Srinaath Ravichandran <sravichandran@deloitte.com>
 *
 * @description Set of Utility functions to query the current breakpoints from javascript and set other global window event handlers
 *
 * Content added to the body element for different breakpoints
 * $small-and-above : 'mobile'
 * $tablet-and-above: 'tablet'
 * $desktop: 'desktop'
 * $large-only: 'large'
 * $xlarge-and-above: 'xlarge'
 *
 * SubscribeUI library
 * https://www.npmjs.com/package/subscribe-ui-event - Subscribe UI event handling
 *
 * @example
 * import {getBreakpoint, onNextFrame} from './breakpoints';
 */

import { subscribe } from "subscribe-ui-event";
import { isHydrated } from "../util/is-hydrated";

export const ORIENTATIONS = {
  portrait: "portrait",
  landscape: "landscape",
};

export const DEVICE_BREAKPOINTS = {
  mobile: 0,
  tablet: 768,
  desktop: 992,
};

export const DEVICES = {
  mobile: "mobile",
  tablet: "tablet",
  desktop: "desktop",
};

export const MAX_GRID_WIDTH = "1372px";
export const TABLET_GRID_BREAKPOINT = `${DEVICE_BREAKPOINTS.tablet}px`;
export const DESKTOP_GRID_BREAKPOINT = `${DEVICE_BREAKPOINTS.desktop}px`;

export const MOBILE_GRID_GAP = 10;
export const TABLET_GRID_GAP = 15;
export const DESKTOP_GRID_GAP = 25;

/**
 * @function
 */
export function getBreakpoint(force) {
  if (typeof window !== "undefined" && (force || isHydrated())) {
    return window
      .getComputedStyle(document.querySelector("body"), ":before")
      .getPropertyValue("content")
      .replace(/"/g, "");
  }

  return "desktop";
}

/**
 * @function
 */
export function getDeviceOrientation(force) {
  if (force || (typeof window !== "undefined" && isHydrated())) {
    if (typeof window.orientation !== "undefined") {
      return window.innerHeight > window.innerWidth
        ? ORIENTATIONS.portrait
        : ORIENTATIONS.landscape;
    }

    return window.screen.height > window.screen.width
      ? ORIENTATIONS.portrait
      : ORIENTATIONS.landscape;
  }

  return ORIENTATIONS.landscape;
}

/**
 * @function
 */
export function getDeviceBreakpoint(force) {
  let device = DEVICES.desktop;

  if (force || (typeof window !== "undefined" && isHydrated())) {
    const mobileBp = 600;
    const tabletBp = 800;
    const orientation = getDeviceOrientation(force);
    const deviceSize =
      orientation === ORIENTATIONS.portrait
        ? window.screen.width
        : window.screen.height;
    device = DEVICES.desktop;
    if (deviceSize > 0) {
      if (deviceSize < mobileBp) {
        device = DEVICES.mobile;
      } else if (deviceSize >= mobileBp && deviceSize <= tabletBp) {
        device = DEVICES.tablet;
      }
    }

    // Additional check to ensure wide screens with a mouse are classified as desktop
    if (
      window.screen.width > tabletBp &&
      window.matchMedia &&
      window.matchMedia("(pointer:fine)").matches
    ) {
      device = DEVICES.desktop;
    }
  }
  return device;
}

/**
 * @function
 */
export function onNextFrame(callback, timeoutVal) {
  let timerValue = 0;
  if (timeoutVal) {
    timerValue = timeoutVal;
  }
  if (process.browser) {
    setTimeout(() => {
      window.requestAnimationFrame(callback);
    }, timerValue);
  }
}

function wrapSubscribe(fn) {
  const wrapped = (...args) => fn(...args);
  wrapped.unsubscribe = () => {};
  return wrapped;
}

/**
 * @function
 */
export function getResizeSubscriber(resizeHandlerBinding, throttleRate) {
  if (!process.browser) return wrapSubscribe(resizeHandlerBinding);

  let currentThrottleRate = 50;
  if (throttleRate) {
    currentThrottleRate = throttleRate;
  }
  return subscribe("resize", resizeHandlerBinding, {
    enableResizeInfo: true,
    useRAF: true,
    throttleRate: currentThrottleRate,
  });
}

/**
 * @function
 */
export function getScrollSubscriber(scrollHandlerBinding, throttleRate) {
  if (!process.browser) return wrapSubscribe(scrollHandlerBinding);

  let tRate = 0;
  if (throttleRate) {
    tRate = throttleRate;
  }
  return subscribe("scroll", scrollHandlerBinding, {
    useRAF: true,
    enableScrollInfo: true,
    throttleRate: tRate,
  });
}

/**
 * @function
 */
export function getScrollEndSubscriber(scrollHandlerBinding) {
  if (!process.browser) return wrapSubscribe(scrollHandlerBinding);

  return subscribe("scrollEnd", scrollHandlerBinding, {
    useRAF: true,
    enableScrollInfo: true,
    throttleRate: 0,
  });
}

/**
 * @function
 * This function detects if the client device is an physical device or not
 */
export function isTouchableDevice() {
  if (typeof window !== "undefined" && isHydrated()) {
    return (
      getDeviceBreakpoint() !== DEVICES.desktop || "ontouchend" in document
    );
  }

  return false;
}

export const isMobile = (breakpoint) =>
  breakpoint === DEVICES.mobile || breakpoint === "small";

export const isDesktop = (breakpoint) =>
  breakpoint === DEVICES.desktop ||
  breakpoint === "large" ||
  breakpoint === "xlarge";

export const isTablet = (breakpoint) => breakpoint === DEVICES.tablet;
